import { createAsyncThunk } from '@reduxjs/toolkit';

import { history } from '../../store';
import callApi from '../../service/callApi';
import { enqueueSnackbar, somethingWrongNotification } from '../notifier/notifierActions';
import sendRequest from '../../service/sendRequest';

const GET_EDITABLE_USER = 'userManagement/GET_EDITABLE_USER';
const GET_USERS = 'userManagement/GET_USERS';
const EDIT_USER = 'userManagement/EDIT_USER';
const CREATE_USER = 'userManagement/CREATE_USER';
const BLOCK_USER = 'userManagement/BLOCK_USER';
const DELETE_USER = 'userManagement/DELETE_USER';
const CHANGE_ADMIN_PASSWORD = 'userManagement/CHANGE_ADMIN_PASSWORD';
const CHANGE_USER_PASSWORD = 'userManagement/CHANGE_USER_PASSWORD';

const GET_DEMO_RECREATE_USERS = 'userManagement/GET_DEMO_RECREATE_USERS';
const CHANGE_REQUESTS_AMOUNT = 'userManagement/CHANGE_REQUESTS_AMOUNT';

export const getUsers = createAsyncThunk(GET_USERS, async (_, { dispatch, getState }) => {
  const { page, size, sort } = getState().userManagement.tableSettings;

  const response = await sendRequest(
    () => callApi.get(`/api/user/management/search?page=${page + 1}&size=${size}&sort=${sort}`),
    {
      onError: () => {
        dispatch(
          enqueueSnackbar({
            message: 'couldntPullUsers',
            options: {
              variant: 'error',
            },
          }),
        );
      },
    },
  );

  return { content: response.data?.content ?? [], total: response.data?.totalElements };
});

export const createUser = createAsyncThunk(CREATE_USER, async ({ name, email, password, roleName }, { dispatch }) => {
  await sendRequest(
    () => callApi.post(`/api/user/management/add-user`, { name, password, roleName, username: email }),
    {
      successStatusCode: 201,
      onError: () => {
        dispatch(
          enqueueSnackbar({
            message: 'couldntCreateUser',
            options: {
              variant: 'error',
            },
          }),
        );
      },
    },
  );

  dispatch(
    enqueueSnackbar({
      message: 'userHasBeenAddedSuccessfully',
      options: {
        variant: 'success',
        messageParams: {
          username: email,
        },
      },
    }),
  );
  history.replace('/users');
});

export const getEditableUser = createAsyncThunk(GET_EDITABLE_USER, async ({ id }, { dispatch }) => {
  const response = await sendRequest(() => callApi.get(`/api/user/management/get-by-id/${id}`), {
    onError: () => {
      dispatch(somethingWrongNotification());
      history.push('/users');
    },
  });

  return response.data;
});

export const editUser = createAsyncThunk(EDIT_USER, async ({ id, name, email, roleName }, { dispatch }) => {
  await sendRequest(
    () =>
      callApi.put(`/api/user/management/update-user-info/${id}`, {
        name,
        roleName,
        username: email,
      }),
    {
      onError: () => {
        dispatch(
          enqueueSnackbar({
            message: 'userWithUsernameWasNoChanged',
            options: {
              variant: 'error',
              messageParams: {
                username: email,
              },
            },
          }),
        );
      },
    },
  );

  dispatch(
    enqueueSnackbar({
      message: 'userHasBeenChangedSuccessfully',
      options: {
        variant: 'success',
        messageParams: {
          username: email,
        },
      },
    }),
  );
  history.replace('/users');
});

export const disableUser = createAsyncThunk(BLOCK_USER, async ({ id, enabled }, { dispatch }) => {
  await sendRequest(() => callApi.post(`/api/user/management/${id}/${enabled ? 'disable' : 'enable'}`), {
    onError: () => {
      dispatch(
        enqueueSnackbar({
          message: enabled ? 'couldntDisableUser' : 'couldntEnableUser',
          options: {
            variant: 'error',
          },
        }),
      );
    },
  });

  dispatch(
    enqueueSnackbar({
      message: enabled ? 'userHasBeenDisabledSuccessfully' : 'userHasBeenEnabledSuccessfully',
      options: {
        variant: 'success',
      },
    }),
  );
  dispatch(getUsers());
});

export const deleteUser = createAsyncThunk(DELETE_USER, async ({ id }, { dispatch }) => {
  await sendRequest(() => callApi.delete(`/api/user/management/${id}`), {
    onError: () => {
      dispatch(
        enqueueSnackbar({
          message: 'couldntRemoveUser',
          options: {
            variant: 'error',
          },
        }),
      );
    },
  });

  dispatch(
    enqueueSnackbar({
      message: 'userHasBeenRemoved',
      options: {
        variant: 'success',
      },
    }),
  );
  dispatch(getUsers());
});

export const changeAdminPassword = createAsyncThunk(CHANGE_ADMIN_PASSWORD, async ({ id, password }, { dispatch }) => {
  await sendRequest(() => callApi.post(`/api/user/management/${id}/update-password`, { password }), {
    onError: () => {
      dispatch(
        enqueueSnackbar({
          message: 'couldntChangePassword',
          options: {
            variant: 'error',
          },
        }),
      );
    },
  });

  dispatch(
    enqueueSnackbar({
      message: 'userPasswordHasBeenUpdated',
      options: {
        variant: 'success',
      },
    }),
  );
});

export const changeUserPassword = createAsyncThunk(
  CHANGE_USER_PASSWORD,
  async ({ username, oldPassword, newPassword }, { dispatch }) => {
    await sendRequest(
      () => callApi.post(`/api/user/management/update-password/${username}`, { oldPassword, newPassword }),
      {
        onError: () => {
          dispatch(
            enqueueSnackbar({
              message: 'couldntChangePassword',
              options: {
                variant: 'error',
              },
            }),
          );
        },
      },
    );

    dispatch(
      enqueueSnackbar({
        message: 'userPasswordHasBeenUpdated',
        options: {
          variant: 'success',
        },
      }),
    );
  },
);

export const getDemoRecreateUsers = createAsyncThunk(GET_DEMO_RECREATE_USERS, async (_, { dispatch, getState }) => {
  const { page, size, sort, query } = getState().userManagement.demoRecreateTableSettings;

  const response = await sendRequest(
    () =>
      callApi.post(`/api/consumer/id8/demo/recreate/search?page=${page + 1}&size=${size}&sort=${sort}&query=${query}`),
    {
      onError: () => {
        dispatch(somethingWrongNotification());
      },
    },
  );

  return { content: response.data?.content ?? [], total: response.data?.totalElements };
});

export const changeRequestsAmount = createAsyncThunk(
  CHANGE_REQUESTS_AMOUNT,
  async ({ id, requests }, { dispatch, rejectWithValue }) => {
    await sendRequest(
      () => callApi.post(`/api/consumer/id8/demo/recreate/set/requests?id=${id}&requests=${requests}`),
      {
        onError: () => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(null);
        },
      },
    );

    dispatch(
      enqueueSnackbar({
        message: 'requestsAmountHasBeenChanged',
        options: {
          variant: 'success',
        },
      }),
    );

    dispatch(getDemoRecreateUsers());
  },
);
